import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit, FiEye, FiX, FiFilter } from 'react-icons/fi';
import visitService from '../../services/visitService';
import { useProject } from '../../context/ProjectContext';
import { VISIT_STATUS, VISIT_TYPES } from '../../utils/constants';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import Pagination from '../Common/Pagination';
import '../Common/DataGrid.css';
import './VisitList.css';

const VisitList = () => {
  const { currentProject: contextProject } = useProject();
  const projectCode = contextProject && contextProject !== 'all'
    ? (typeof contextProject === 'string' ? contextProject : contextProject?.projectCode ?? contextProject?.ProjectCode)
    : null;
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    visitType: '',
    siteId: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchVisits();
  }, [page, pageSize, filters, projectCode]);

  const fetchVisits = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        pageNumber: page,
        pageSize: pageSize,
        ...filters,
      });

      const response = await visitService.getAll(projectCode || undefined);
      setVisits(response);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load visits');
      console.error('Error fetching visits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ status: '', visitType: '', siteId: '' });
    setPage(1);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case VISIT_STATUS.COMPLETED:
        return 'badge-success';
      case VISIT_STATUS.ONGOING:
        return 'badge-info';
      case VISIT_STATUS.CONFIRMED:
        return 'badge-warning';
      case VISIT_STATUS.CANCELLED:
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="visit-list">
      <div className="page-header">
        
        <Link to="/visits/new" className="btn btn-primary">
          <FiPlus size={20} /> Schedule New Visit
        </Link>
      </div>

      {error && <Alert type="error" message={error} />}

      {/* Filters */}
      <div className="filters-section">
        <button
          className="btn btn-secondary"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FiFilter size={18} />
          Filters
        </button>

        {showFilters && (
          <div className="filter-panel">
            <div className="filter-group">
              <label>Visit Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                {Object.entries(VISIT_STATUS).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Visit Type</label>
              <select
                value={filters.visitType}
                onChange={(e) => handleFilterChange('visitType', e.target.value)}
              >
                <option value="">All Types</option>
                {Object.entries(VISIT_TYPES).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <button className="btn btn-outline" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Toolbar: sort/filter above table with default size (system-wide) */}
      <div className="data-grid-toolbar">
        <div className="data-grid-controls">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="form-select page-size-select data-grid-page-size"
          >
            <option value={5}>5 items</option>
            <option value={10}>10 items</option>
            <option value={25}>25 items</option>
            <option value={50}>50 items</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Site</th>
              <th>Visit Type</th>
              <th>Scheduled Date</th>
              <th>Status</th>
              <th>CRA</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visits.map((visit) => (
              <tr key={visit.scheduledVisitID}>
                <td>{visit.siteName}</td>
                <td>{VISIT_TYPES[visit.visitType]}</td>
                <td>{formatDateDDMMMYYYY(visit.plannedStartDate)}</td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(visit.visitStatus)}`}>
                    {visit.visitStatus}
                  </span>
                </td>
                <td>{visit.craName}</td>
                <td className="actions">
                  <Link to={`/visits/${visit.scheduledVisitID}`} className="action-btn" title="View">
                    <FiEye size={18} />
                  </Link>
                  <Link to={`/visits/${visit.scheduledVisitID}/edit`} className="action-btn" title="Edit">
                    <FiEdit size={18} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visits.length === 0 && !loading && (
        <div className="empty-state">
          <p>No visits found</p>
          <Link to="/visits/new" className="btn btn-primary">
            Schedule First Visit
          </Link>
        </div>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        totalItems={visits.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        showPageSize={false}
      />
    </div>
  );
};

export default VisitList;